import React, { useEffect, useState, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  changeStatus,
  getContactData,
  getContactStatuses,
  getAssigneesDropdown,
  addAssigneesToContact,
  updateUserContactGroup,
  getLeadDispositions,
  changeLeadDisposition
} from "../../features/customer/customerSlice"
import { fetchContactGroupsDropdownInfo, getContactGroupsForFilterUtils } from "../../features/contactGroups/contactGroups"
import { getContactLinksByUserId, deactivateContactLink } from "../../features/contactLinks/contactLinks"
import { showSnackBar } from "../../components/Snackbar/Snackbar"
import { useMyContextLoading } from "../../components/LoadingContextProvider/LoadingContextProvider"
import { formatFullDateUTC, formatDateOnlyField } from "../../utils/date"
import { formatMoney } from "../../utils/amount"
import UserStatusCell from "../Contacts/Table/Contact/StatusCell"
import { NavLink } from "react-router-dom"
import PopupWithActions from "../../components/Popup/PopupWithAction"
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal"
import { formatUserWithContactGroups, splitAssignees } from "../../utils/assignees"
import { StorageGetItem, USER } from "../../lib/storage"
import { useIsSupervisor } from "../../hooks/user"
import NotesArea from "../../components/NotesArea/NotesArea"
import Card from "../../components/Card/Card"
import Dropdown from "../../components/Dropdown/Dropdown"
import Button from "../../components/Button/Button"
import { togglePlanStatus } from "../../features/payment/paymentSlice"
import { LuTrash2, LuChevronDown, LuAlertTriangle } from "react-icons/lu"
import { REVERSAL_STATUS_VALUES } from "../../constants/reversalStatuses"
import { DEMOTION_GUARD_FLAG, RESTORE_SKIP_TEXT } from "../../constants/demotionGuard"
import { selectFeatureFlag } from "../../features/featureFlags/selectors"
import { AFFILIATE_ASSIGNEE_SPLIT, CONTACT_ACTIVE_DEDUP_BY_SSN, LEAD_DISPOSITION_PIPELINE } from "../../config/featureFlagKeys"
import AssigneeSplit from "./AssigneeSplit"
import StatusChangeConfirm from "../../components/StatusChangeConfirm/StatusChangeConfirm"
import LeadStatusConfirm from "../../components/LeadStatus/LeadStatusConfirm"
import LeadStatusChip from "../../components/LeadStatus/LeadStatusChip"
import { changeDispositionOutcome, changedByLine, isLeadContact } from "../../components/LeadStatus/leadStatus"

const SideBarInfo = ({ userId, triggerUpdateSidebar = false, setNoteCreateTrigger }) => {
  const dispatch = useDispatch()
  const demotionGuardOn = useSelector(selectFeatureFlag(DEMOTION_GUARD_FLAG))
  const activeDedupOn = useSelector(selectFeatureFlag(CONTACT_ACTIVE_DEDUP_BY_SSN))
  const pipelineOn = useSelector(selectFeatureFlag(LEAD_DISPOSITION_PIPELINE))
  const assigneeSplitOn = useSelector(selectFeatureFlag(AFFILIATE_ASSIGNEE_SPLIT))
  const [userData, setUserData] = useState("")
  const { triggerLoading } = useMyContextLoading()
  const isSupervisor = useIsSupervisor()
  const [reqError, setReqError] = useState('')

  const [openChangeStatusModal, setOpenChangeStatusModal] = useState(false)
  const [anchorPaymentPlan, setAnchorPaymentPlan] = useState(null)
  const [openChangePlanModal, setOpenChangePlanModal] = useState(false)
  // The value the user actually picked. Derived state would re-read userData, which can
  // refresh while the modal is open and silently flip the target under the cursor.
  const [planTarget, setPlanTarget] = useState(null)
  const [anchorStatuses, setAnchorStatuses] = useState(null)
  const [openStatuses, setOpenStatuses] = useState(false)
  const [changeStatusId, setChangeStatusId] = useState(false)
  const [statuses, setStatuses] = useState([])
  const [userRole, setUserRole] = useState('')
  const [dispositions, setDispositions] = useState([])
  const [anchorDispositions, setAnchorDispositions] = useState(null)
  const [openDispositions, setOpenDispositions] = useState(false)
  const [openChangeDispositionModal, setOpenChangeDispositionModal] = useState(false)
  const [changeDispositionId, setChangeDispositionId] = useState(null)
  // Inline assignee dropdown states (for affiliates)
  const [showAssigneeForm, setShowAssigneeForm] = useState(false)
  const [assigneesDropdownOptions, setAssigneesDropdownOptions] = useState([])
  const [selectedAssignee, setSelectedAssignee] = useState('')
  const [existingAssignees, setExistingAssignees] = useState([])
  const [loadingDropdown, setLoadingDropdown] = useState(false)
  const [affiliateContactGroups, setAffiliateContactGroups] = useState([])
  const [currentUserId, setCurrentUserId] = useState('')
  const [linkedContacts, setLinkedContacts] = useState([])
  const [contactGroupOptions, setContactGroupOptions] = useState([])
  const [selectedContactGroup, setSelectedContactGroup] = useState('')
  const [editingAssignees, setEditingAssignees] = useState([])
  const [showAGAEditForm, setShowAGAEditForm] = useState(false)
  const [isGroupOwner, setIsGroupOwner] = useState(false)

  useEffect(() => {
    StorageGetItem(USER).then(result => {
      setUserRole(result?.role)
      setCurrentUserId(result?._id || '')
      setIsGroupOwner(!!result?.isGroupOwner)
      if (result?.role === 'affiliate') {
        setAffiliateContactGroups(result?.contactGroups || [])
      }
    })
  }, [])

  useEffect(() => {
    if (userRole === 'customer' || userRole === 'user') {
      dispatch(fetchContactGroupsDropdownInfo()).then(result => {
        if (result?.payload?.data) {
          setContactGroupOptions(result.payload.data)
        }
      })
    } else if (userRole === 'affiliateGroupsAdmin') {
      dispatch(getContactGroupsForFilterUtils()).then(result => {
        if (result?.payload?.data) {
          setContactGroupOptions(result.payload.data)
        }
      })
    }
  }, [userRole, dispatch])

  const getStatuses = async () => {
    try {
      let result = await dispatch(getContactStatuses())
      setStatuses(result?.payload?.data)
    } catch (error) {
      console.error('An unexpected error occurred:', error)
    }
  }

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (anchorStatuses && !anchorStatuses.contains(event.target)) {
        setOpenStatuses(null)
        setAnchorStatuses(null)
      }
    }

    document.addEventListener('click', handleDocumentClick)

    return () => {
      document.removeEventListener('click', handleDocumentClick)
    }
  }, [anchorStatuses])

  const fetchLinkedContacts = async () => {
    try {
      const response = await dispatch(getContactLinksByUserId(userId))
      const data = Array.isArray(response?.payload) ? response.payload : response?.payload?.data || []
      setLinkedContacts(data)
    } catch (error) {
      console.error('Failed to load linked contacts')
    }
  }

  const fetchData = async () => {
    try {
      const resultAction = await dispatch(getContactData(userId))
      setUserData(resultAction?.payload?.data)
    } catch (error) {
      console.error('Error dispatching getContactById:', error)
    }
  }

  useEffect(() => {
    const handleReqError = () => {
      if (reqError?.length > 0) {
        showSnackBar("error", reqError)
        setReqError('')
      }
    }

    handleReqError()
  }, [reqError])

  useEffect(() => {
    setUserData("")
    setAnchorPaymentPlan(null)
    setOpenChangePlanModal(false)
    setPlanTarget(null)
    fetchData()
    getStatuses()
    fetchLinkedContacts()
    dispatch(getLeadDispositions()).then(res => {
      if (res?.payload?.data) setDispositions(res.payload.data)
    })
  }, [userId])

  const trigger = () => {
    fetchData()
  }

  const isFirstSidebarRender = useRef(true)
  useEffect(() => {
    if (isFirstSidebarRender.current) {
      isFirstSidebarRender.current = false
      return
    }
    trigger()
  }, [triggerUpdateSidebar])


  const handleClickStatuses = (event, id) => {
    setOpenStatuses(id)
    setAnchorStatuses(event.currentTarget)
  }

  const handleChangeStatus = async () => {
    triggerLoading(true)
    try {
      const result = await dispatch(changeStatus({ statusId: changeStatusId, userId }))
      if (isReversalPick && result?.error) {
        showSnackBar("error", result?.payload || "This status could not be set for this client.")
      } else if (activeDedupOn && result?.error) {
        showSnackBar("error", typeof result.payload === 'string' ? result.payload : 'Customer status could not be changed.')
      } else {
        showSnackBar("success", "Customer status successfully changed!")
      }
      trigger()
    } catch (error) {
      showSnackBar('error', 'An unexpected error occurred:')
    }
    triggerLoading(false)
  }

  const handleGuardedChangeStatus = async (body) => {
    triggerLoading(true)
    try {
      const result = await dispatch(changeStatus({ statusId: changeStatusId, userId, ...(body || {}) }))
      if (result?.error) {
        showSnackBar("error", typeof result.payload === 'string' ? result.payload : 'Customer status could not be changed.')
      } else {
        const planRestore = result?.payload?.data?.planRestore
        let message = "Customer status successfully changed!"
        if (planRestore) {
          message += planRestore.restored
            ? " Billing resumed."
            : ` Billing was not resumed: ${RESTORE_SKIP_TEXT[planRestore.reason] || 'check Payment Plan'}.`
        }
        showSnackBar("success", message)
      }
      trigger()
    } catch (error) {
      showSnackBar('error', 'An unexpected error occurred:')
    }
    triggerLoading(false)
  }

  const handleClickPaymentPlan = (event) => {
    if (anchorPaymentPlan) {
      setAnchorPaymentPlan(null)
    } else {
      setAnchorPaymentPlan(event.currentTarget)
    }
  }

  // The API is a toggle, not a setter, so picking the value the plan is already on
  // must be a no-op rather than a request that would flip it to the other one.
  const handlePickPaymentPlan = (nextActive) => {
    setAnchorPaymentPlan(null)
    if (nextActive === (userData?.paymentPlan === 'active')) return
    setPlanTarget(nextActive)
    setOpenChangePlanModal(true)
  }

  const handleChangePlanStatus = async () => {
    // The plan may have moved since the popup was opened; do not toggle it away from
    // what the user actually chose.
    if (planTarget === null || planTarget === (userData?.paymentPlan === 'active')) {
      showSnackBar('error', 'The payment plan changed while this was open. Please try again.')
      return
    }
    triggerLoading(true)
    try {
      // createAsyncThunk with rejectWithValue RESOLVES with a rejected action rather than
      // throwing, so a bare try/catch would report success on every failure.
      //
      // Send the state the operator picked rather than letting the server invert. This
      // switch only ever offered active and inactive, so on a canceled plan an inversion
      // would quietly activate it from a control that never mentioned cancellation.
      const result = await dispatch(togglePlanStatus({ userId, status: planTarget ? 'active' : 'inactive' }))
      if (result?.error) {
        showSnackBar('error', typeof result.payload === 'string' ? result.payload : 'Failed to change payment plan status')
      } else {
        showSnackBar('success', 'Payment plan status successfully changed!')
        trigger()
      }
    } catch (error) {
      showSnackBar('error', 'Failed to change payment plan status')
    } finally {
      triggerLoading(false)
    }
  }

  const handleClickDispositions = (event) => {
    if (openDispositions) {
      setOpenDispositions(false)
      setAnchorDispositions(null)
    } else {
      setOpenDispositions(true)
      setAnchorDispositions(event.currentTarget)
    }
  }

  const handleChangeDisposition = async () => {
    triggerLoading(true)
    try {
      await dispatch(changeLeadDisposition({ userId, dispositionId: changeDispositionId }))
      showSnackBar("success", "Disposition successfully changed!")
      trigger()
    } catch (error) {
      showSnackBar('error', 'An unexpected error occurred')
    }
    setOpenChangeDispositionModal(false)
    triggerLoading(false)
  }

  const handleChangeLeadStatus = async ({ reason } = {}) => {
    triggerLoading(true)
    try {
      const result = await dispatch(changeLeadDisposition({ userId, dispositionId: changeDispositionId, reason }))
      const outcome = changeDispositionOutcome(result)
      showSnackBar(outcome.ok ? 'success' : 'error', outcome.message)
      trigger()
    } catch (error) {
      showSnackBar('error', 'An unexpected error occurred')
    }
    setOpenChangeDispositionModal(false)
    triggerLoading(false)
  }

  const handleCloseStatuses = () => {
    setOpenStatuses(false)
  }

  useEffect(() => {
    const fetchDropdownData = async () => {
      const affiliateOpened = userRole === 'affiliate' && !isGroupOwner && showAssigneeForm
      const agaOpened = (userRole === 'affiliateGroupsAdmin' || (userRole === 'affiliate' && isGroupOwner)) && showAGAEditForm
      if (!affiliateOpened && !agaOpened) return
      setLoadingDropdown(true)
      try {
        const dropdownResponse = await dispatch(getAssigneesDropdown())
        if (dropdownResponse?.payload?.data) {
          setAssigneesDropdownOptions(dropdownResponse.payload.data)
        }
      } catch (error) {
        console.error('Failed to load assignees dropdown')
      }
      setLoadingDropdown(false)
    }
    fetchDropdownData()
  }, [userRole, isGroupOwner, showAssigneeForm, showAGAEditForm, dispatch])

  // Update existing assignees when userData changes
  useEffect(() => {
    if (userData?.assignees) {
      setExistingAssignees(userData.assignees.map(a => a._id || a))
    }
  }, [userData])

  useEffect(() => {
    const source = userData?.officeGroup
      ? [userData.officeGroup]
      : (userData?.contactGroups?.length ? userData.contactGroups : userData?.creatorContactGroups) || []

    const first = source[0]
    setSelectedContactGroup(first ? (first._id || first) : '')
  }, [userData])

  const canEditAssignees = userRole === 'affiliateGroupsAdmin' || (userRole === 'affiliate' && isGroupOwner)

  // Filter dropdown to exclude already assigned users
  const filteredDropdown = assigneesDropdownOptions.filter(
    a => !existingAssignees.includes(a.value)
  )

  // Filter assignees for affiliates - show only those with shared contact groups
  const getFilteredAssigneesForAffiliate = () => {
    if (!userData?.assignees || affiliateContactGroups.length === 0) {
      return []
    }
    return userData.assignees.filter(assignee => {
      const assigneeGroupIds = assignee.contactGroups?.map(g => g._id || g) || []
      return assigneeGroupIds.some(groupId => affiliateContactGroups.includes(groupId))
    })
  }

  const showAssigneeSplit = assigneeSplitOn && userData?.creatorRole !== undefined
  const assigneeSplitView = showAssigneeSplit
    ? splitAssignees({
      creator: userData?.creator,
      creatorRole: userData?.creatorRole,
      assignees: (userRole === 'affiliate' ? getFilteredAssigneesForAffiliate() : userData?.assignees) || [],
      officeName: userData?.officeGroup?.name || '',
    })
    : null

  const handleReassign = async () => {
    if (!selectedAssignee) return
    triggerLoading(true)
    try {
      const newAssigneesList = [...existingAssignees, selectedAssignee]
      const response = await dispatch(addAssigneesToContact({ userId, assignees: newAssigneesList }))
      if (response.error) {
        showSnackBar("error", response.payload || "Failed to update assignee")
      } else {
        showSnackBar("success", "Assignee updated!")
        await fetchData()
        setSelectedAssignee('')
        setShowAssigneeForm(false)
      }
    } catch (error) {
      showSnackBar("error", "Failed to update assignee")
    }
    triggerLoading(false)
  }

  const openAGAEdit = () => {
    setEditingAssignees(userData?.assignees ? [...userData.assignees] : [])
    setSelectedAssignee('')
    setShowAGAEditForm(true)
  }

  const closeAGAEdit = () => {
    setShowAGAEditForm(false)
    setEditingAssignees([])
    setSelectedAssignee('')
  }

  const handleRemoveEditingAssignee = (assigneeId) => {
    setEditingAssignees(prev => prev.filter(a => (a._id || a) !== assigneeId))
  }

  const handleAddEditingAssignee = (assigneeId) => {
    if (!assigneeId) return
    const alreadyIn = editingAssignees.some(a => (a._id || a) === assigneeId)
    if (alreadyIn) return
    const fromDropdown = assigneesDropdownOptions.find(o => o.value === assigneeId)
    const newEntry = fromDropdown
      ? { _id: assigneeId, firstName: fromDropdown.label?.split(' ')[0] || '', lastName: fromDropdown.label?.split(' ').slice(1).join(' ') || '' }
      : { _id: assigneeId }
    setEditingAssignees(prev => [...prev, newEntry])
    setSelectedAssignee('')
  }

  const handleSaveAGAAssignees = async () => {
    if (editingAssignees.length === 0) {
      showSnackBar('error', 'At least one assignee is required')
      return
    }
    triggerLoading(true)
    try {
      const ids = editingAssignees.map(a => a._id || a)
      const response = await dispatch(addAssigneesToContact({ userId, assignees: ids }))
      if (response.error) {
        showSnackBar('error', response.payload || 'Failed to update assignees')
      } else {
        showSnackBar('success', 'Assignees updated!')
        await fetchData()
        closeAGAEdit()
      }
    } catch (error) {
      showSnackBar('error', 'Failed to update assignees')
    }
    triggerLoading(false)
  }

  const agaEditDropdownOptions = assigneesDropdownOptions.filter(
    o => !editingAssignees.some(a => (a._id || a) === o.value)
  )

  const handleContactGroupChange = async (newValue) => {
    setSelectedContactGroup(newValue)
    try {
      const result = await dispatch(updateUserContactGroup({ userId, contactGroupId: newValue }))
      if (result.error) {
        showSnackBar('error', result.payload || 'Failed to update contact group')
        setSelectedContactGroup(userData?.contactGroups?.[0]?._id || '')
      } else {
        showSnackBar('success', 'Contact group updated')
        await fetchData()
      }
    } catch {
      showSnackBar('error', 'Failed to update contact group')
      setSelectedContactGroup(userData?.contactGroups?.[0]?._id || '')
    }
  }

  // The sidebar can show a group inferred from the creator snapshot, which is not stored on the
  // contact. Confirming that same group is the only way to pin it, and MUI fires no change event
  // when the clicked option already is the value.
  const handleContactGroupReselect = (clickedValue) => {
    if (clickedValue && clickedValue === selectedContactGroup) handleContactGroupChange(clickedValue)
  }

  // PUT /plans/status/users/:id is gated to hasRoles(['customer','user']); showing the
  // control to anyone else would only produce a 403 on click.
  // It also stays inert until the contact has loaded: the endpoint is a toggle, so acting
  // on a not-yet-known current value would flip the plan the wrong way.
  // A contact with no plan document comes back as paymentPlan:null and renders as
  // "Inactive", indistinguishable from a real inactive plan — but the toggle endpoint
  // answers 200 without changing anything, so offering the control would only lie.
  const isAffiliate = userRole === 'affiliate'
  const pickableStatuses = isAffiliate
    ? statuses?.filter((status) => REVERSAL_STATUS_VALUES.includes(status?.value))
    : statuses
  const canPickStatus = !isAffiliate || (userData?.canSetReversalStatus === true && pickableStatuses?.length > 0)
  const pickedStatus = statuses?.find((status) => status?._id === changeStatusId)
  const isReversalPick = isAffiliate && REVERSAL_STATUS_VALUES.includes(pickedStatus?.value)
  const pickedDisposition = dispositions.find((disp) => disp?._id === changeDispositionId)
  const leadChangeLine = pipelineOn ? changedByLine(userData?.dispositionChange) : null

  const isPlanActive = userData?.paymentPlan === 'active'
  const isPlanCanceled = userData?.paymentPlan === 'canceled'
  // This control offers Active and Inactive only, and the API refuses to leave canceled without an
  // explicit pick, so on a canceled plan it could only ever produce a click that does nothing.
  // Cancellation is undone from the Payment tab, where Canceled is one of the three options.
  const canEditPaymentPlan = !!userData?._id && !!userData?.paymentPlan && !isPlanCanceled
    && (userRole === 'customer' || userRole === 'user')
  // Canceled is a settled end state rather than a fault, so it reads neutral. Red stays for
  // inactive, the one an operator may still need to act on.
  const paymentPlanTone = isPlanActive
    ? { background: 'rgba(18,183,106,0.14)', color: 'var(--color-success-text)' }
    : isPlanCanceled
      ? { background: 'var(--color-gray-200)', color: 'var(--color-gray-600)' }
      : { background: 'rgba(240,68,56,0.14)', color: 'var(--color-error-text)' }
  const paymentPlanBadge = (
    <span
      data-testid="payment-plan-badge"
      className="inline-flex items-center text-[12px] font-semibold py-1 px-2.5 rounded-md"
      style={paymentPlanTone}>
      {isPlanActive ? "Active" : isPlanCanceled ? "Canceled" : "Inactive"}
    </span>
  )

  return (
    <Card className="flex flex-col px-6 py-5 w-[360px] gap-4">
      <div className="flex flex-col gap-4 text-text-secondary">
        <h6 className="text-[12px] font-semibold uppercase tracking-[0.04em] text-text-secondary">Status</h6>

        <div className="grid grid-cols-[112px_1fr] gap-3 items-start text-[14px]">
          Status Name:
          <div className="flex items-center gap-2">
            <div className={canPickStatus ? "cursor-pointer" : ""}
              onClick={(event) => canPickStatus && handleClickStatuses(event, userData._id)}>
              <UserStatusCell statusObj={userData?.status} arrow={canPickStatus} isOpen={!!anchorStatuses} />
            </div>
          </div>
        </div>
        {pipelineOn && isLeadContact(userData?.status) && (
          <div className="grid grid-cols-[112px_1fr] gap-3 items-start text-[14px]">
            Lead Status:
            <div className="flex flex-col gap-1 min-w-0">
              <div className="flex items-center gap-2">
                {userData?.dispositionData?._id ? (
                  <div className="cursor-pointer" onClick={handleClickDispositions}>
                    <UserStatusCell statusObj={userData?.dispositionData} arrow={true} isOpen={!!anchorDispositions} outlined />
                  </div>
                ) : (
                  <LeadStatusChip onClick={handleClickDispositions} />
                )}
              </div>
              {leadChangeLine && (
                <span className="text-[12px] text-text-secondary">{leadChangeLine}</span>
              )}
              {userData?.dispositionChange?.reason && (
                <span className="text-[12px] text-text-secondary break-words">Reason: {userData.dispositionChange.reason}</span>
              )}
            </div>
          </div>
        )}
        {!pipelineOn && userData?.dispositionData && (
          <div className="grid grid-cols-[112px_1fr] gap-3 items-start text-[14px]">
            Lead Status:
            <div className="flex items-center gap-2">
              <div className="cursor-pointer" onClick={handleClickDispositions}>
                <UserStatusCell statusObj={userData?.dispositionData} arrow={true} isOpen={!!anchorDispositions} outlined />
              </div>
            </div>
          </div>
        )}
        <div className="grid grid-cols-[112px_1fr] gap-3 items-start text-[14px]">
          Payment Plan:
          {canEditPaymentPlan ? (
            <div
              data-testid="payment-plan-trigger"
              role="button"
              tabIndex={0}
              aria-haspopup="listbox"
              aria-expanded={!!anchorPaymentPlan}
              aria-label="Change payment plan status"
              className="flex items-center gap-1 cursor-pointer max-w-max"
              onClick={handleClickPaymentPlan}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  handleClickPaymentPlan(event)
                }
              }}
            >
              {paymentPlanBadge}
              <LuChevronDown
                size={16}
                className={`text-text-secondary transition-transform ${anchorPaymentPlan ? 'rotate-180' : ''}`}
              />
            </div>
          ) : paymentPlanBadge}
        </div>
        <div className="grid grid-cols-[112px_1fr] gap-3 items-start text-[14px]">
          Doc Status:
          <span className="inline-flex items-center text-[12px] font-semibold py-1 px-2.5 rounded-md"
            style={userData?.docStatus === 'Signed'
              ? { background: 'rgba(18,183,106,0.14)', color: 'var(--color-success-text)' }
              : userData?.docStatus === 'Sent'
                ? { background: 'rgba(247,144,9,0.14)', color: 'var(--color-warning-text)' }
                : { background: 'rgba(240,68,56,0.14)', color: 'var(--color-error-text)' }}>
            {userData?.docStatus}
          </span>
        </div>
        <div className="grid grid-cols-[112px_1fr] gap-3 items-start text-[14px]">
          Payment Method:
          <span className="inline-flex items-center text-[12px] font-semibold py-1 px-2.5 rounded-md"
            style={userData?.paymentMethod
              ? { background: 'var(--color-gray-200)', color: 'var(--color-text-primary)' }
              : { background: 'rgba(240,68,56,0.14)', color: 'var(--color-error-text)' }}>
            {userData?.paymentMethod || "-"}
          </span>
        </div>
      </div>
      <div className="border-t border-divider my-2"></div>

      <div className="flex flex-col gap-4 text-text-secondary">
        <div className="flex justify-between">
          <h6 className="text-[12px] font-semibold uppercase tracking-[0.04em] text-text-secondary">Customer Info</h6>
          {
            userRole !== 'affiliate' && <NavLink to={`/contact/view/${userData?._id}`}>
              <div
                className="cursor-pointer text-[15px] font-bold underline text-primary-main hover:text-primary-main/80">
                View more
              </div>
            </NavLink>}
        </div>
        <div className="grid grid-cols-[112px_1fr] gap-3 items-start text-[14px]">
          Primary Phone:
          <p className="text-text-primary text-[14px] font-medium break-words min-w-0">
            {userData?.primaryPhone || '-'}
          </p>
        </div>
        <div className="grid grid-cols-[112px_1fr] gap-3 items-start text-[14px]">
          Portal login email:
          <p className="text-text-primary text-[14px] font-medium break-words min-w-0">
            {userData?.secondaryEmail || '-'}
          </p>
        </div>
        <div className="grid grid-cols-[112px_1fr] gap-3 items-start text-[14px]">
          SSN:
          <p className="text-text-primary text-[14px] font-medium break-words min-w-0">
            {userData?.ssn || '-'}
          </p>
        </div>
        <div className="grid grid-cols-[112px_1fr] gap-3 items-start text-[14px]">
          Date of birth:
          <p className="text-text-primary text-[14px] font-medium break-words min-w-0">
            {formatDateOnlyField(userData?.dateOfBirth) || '-'}
          </p>
        </div>
        <div className="grid grid-cols-[112px_1fr] gap-3 items-start text-[14px]">
          Monthly Payment:
          <p className="text-text-primary text-[14px] font-medium break-words min-w-0">
            {userData?.monthlyPayment ? formatMoney(userData.monthlyPayment) : '-'}
          </p>
        </div>
        <div className="grid grid-cols-[112px_1fr] gap-3 items-start text-[14px]">
          Next Payment Date:
          <p className="text-text-primary text-[14px] font-medium break-words min-w-0">
            {userData?.nextPaymentDate ? formatFullDateUTC(userData.nextPaymentDate) : '-'}
          </p>
        </div>
        <div className="grid grid-cols-[112px_1fr] gap-3 items-start text-[14px]">
          Credit Score:
          <p className="text-text-primary text-[14px] font-medium break-words min-w-0">
            {userData?.creditScore || '-'}
          </p>
        </div>
        <div className="grid grid-cols-[112px_1fr] gap-3 items-start text-[14px]">
          Lead Source:
          <p className="text-text-primary text-[14px] font-medium break-words min-w-0">
            {userData?.acquisitionSource || '-'}
          </p>
        </div>
        <div className="grid grid-cols-[112px_1fr] gap-3 items-start text-[14px]">
          Mailing Address:
          <p className="text-text-primary text-[14px] font-medium break-words min-w-0">
            {userData?.address?.address
              ? [
                  userData.address.address,
                  userData.address.aptNumber,
                  userData.address.city,
                  userData.address.state,
                  userData.address.zip
                ].filter(Boolean).join(', ')
              : '-'}
          </p>
        </div>
        <div className="border-t border-divider my-2"></div>
      </div>
      {(userRole === 'customer' || userRole === 'user' || userRole === 'affiliateGroupsAdmin') && (
        <div className="flex flex-col gap-3">
          <h6 className="text-[12px] font-semibold uppercase tracking-[0.04em] text-text-secondary">Contact Group</h6>
          <Dropdown
            value={selectedContactGroup}
            setValue={handleContactGroupChange}
            onItemClick={handleContactGroupReselect}
            menuItems={contactGroupOptions}
            placeholder="Select contact group"
            className="w-full"
            disabled={userRole === 'affiliateGroupsAdmin' && contactGroupOptions.length === 0}
          />
          <div className="border-t border-divider my-2"></div>
        </div>
      )}
      <div className="flex flex-col gap-3">
        <div className="flex justify-between items-center">
          <h6 className="text-[12px] font-semibold uppercase tracking-[0.04em] text-text-secondary">Assignees</h6>
          {/* Internal users: Edit button (existing behavior) */}
          {(userRole === 'customer' || userRole === 'user') && (
            <NavLink to={`/contact/edit/${userData?._id}`}>
              <div className="cursor-pointer text-[15px] font-bold underline text-primary-main hover:text-primary-main/80">
                Edit
              </div>
            </NavLink>
          )}
          {/* Affiliates: Add button (only if affiliate is currently assigned) */}
          {userRole === 'affiliate' && !isGroupOwner && existingAssignees.includes(currentUserId) && (
            <div
              onClick={() => setShowAssigneeForm(!showAssigneeForm)}
              className="cursor-pointer text-[15px] font-bold underline text-primary-main hover:text-primary-main/80"
            >
              {showAssigneeForm ? 'Cancel' : 'Add'}
            </div>
          )}
          {canEditAssignees && (
            <div
              onClick={() => showAGAEditForm ? closeAGAEdit() : openAGAEdit()}
              className="cursor-pointer text-[15px] font-bold underline text-primary-main hover:text-primary-main/80"
            >
              {showAGAEditForm ? 'Cancel' : 'Edit'}
            </div>
          )}
        </div>

        {assigneeSplitView && (
          <AssigneeSplit affiliate={assigneeSplitView.affiliate} internal={assigneeSplitView.internal} userRole={userRole} />
        )}

        {/* Internal users: Show assignees list */}
        {!assigneeSplitView && userRole !== 'affiliate' && (
          <div className="flex flex-col text-[14px] text-text-secondary overflow-hidden">
            {userData?.assignees?.length > 0
              ? userData?.assignees?.map(assignee =>
                <span key={assignee?._id || assignee?.id}
                  className="truncate overflow-hidden text-ellipsis whitespace-normal line-clamp-2">
                  {formatUserWithContactGroups({ user: assignee, userRole })}
                </span>
              )
              : <span className="truncate whitespace-nowrap overflow-hidden text-ellipsis">
                {userData?.creator?.firstName && userData?.creator?.lastName
                  ? userData?.creator?.firstName + ' ' + userData?.creator?.lastName
                  : '-'}
              </span>
            }
          </div>
        )}

        {/* Affiliates: Show filtered assignees list (only from shared contact groups) */}
        {!assigneeSplitView && userRole === 'affiliate' && (
          <div className="flex flex-col text-[14px] text-text-secondary overflow-hidden">
            {getFilteredAssigneesForAffiliate().length > 0
              ? getFilteredAssigneesForAffiliate().map(assignee =>
                <span key={assignee?._id || assignee?.id}
                  className="truncate overflow-hidden text-ellipsis whitespace-normal line-clamp-2">
                  {assignee?.firstName} {assignee?.lastName}
                </span>
              )
              : <span>-</span>
            }
          </div>
        )}

        {userRole === 'affiliate' && showAssigneeForm && (
          <div className="flex flex-col gap-2">
            {loadingDropdown ? (
              <p className="text-text-secondary text-sm">Loading...</p>
            ) : filteredDropdown.length === 0 ? (
              <p className="text-text-secondary text-sm">No available assignees</p>
            ) : (
              <div className="flex flex-col gap-2">
                <Dropdown
                  value={selectedAssignee}
                  setValue={setSelectedAssignee}
                  menuItems={filteredDropdown}
                  placeholder="Select assignee"
                  className="w-full"
                />
                <div className='w-full flex justify-end'>
                  <Button
                    onClick={handleReassign}
                    size="md"
                    width="w-max"
                    disabled={!selectedAssignee}
                    type='text'
                  >
                    Save
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {canEditAssignees && showAGAEditForm && (
          <div className="flex flex-col gap-3 mt-2">
            <div className="flex flex-wrap gap-2">
              {editingAssignees.length === 0
                ? <span className="text-text-secondary text-sm">No assignees selected</span>
                : editingAssignees.map(a => (
                  <span
                    key={a._id || a}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary-main/10 text-primary-main text-[12px] font-medium"
                  >
                    {`${a.firstName || ''} ${a.lastName || ''}`.trim() || (a._id || a)}
                    <button
                      type="button"
                      onClick={() => handleRemoveEditingAssignee(a._id || a)}
                      className="ml-1 hover:text-error-main"
                      aria-label="Remove assignee"
                    >
                      ×
                    </button>
                  </span>
                ))
              }
            </div>
            {loadingDropdown ? (
              <p className="text-text-secondary text-sm">Loading...</p>
            ) : agaEditDropdownOptions.length === 0 ? (
              <p className="text-text-secondary text-sm">No more assignees available</p>
            ) : (
              <Dropdown
                value={selectedAssignee}
                setValue={(value) => handleAddEditingAssignee(value)}
                menuItems={agaEditDropdownOptions}
                placeholder="Add assignee"
                className="w-full"
              />
            )}
            <div className='w-full flex justify-end'>
              <Button
                onClick={handleSaveAGAAssignees}
                size="md"
                width="w-max"
                disabled={editingAssignees.length === 0}
                type='text'
              >
                Save
              </Button>
            </div>
          </div>
        )}
      </div>
      {linkedContacts.length > 0 && (
        <>
          <div className="border-t border-divider my-2"></div>
          <div className="flex flex-col gap-3">
            <h6 className="text-[12px] font-semibold uppercase tracking-[0.04em] text-text-secondary">Co-Applicant</h6>
            {linkedContacts.map((link) => {
              const contact = link.linkedContact?.firstName ? link.linkedContact : link.primaryContact
              return (
                <div key={link._id} className="grid grid-cols-[112px_1fr] gap-3 items-start text-[14px] text-text-secondary">
                  <div className="flex flex-col">
                    <span className="font-semibold text-text-primary">
                      {contact?.firstName} {contact?.lastName}
                    </span>
                    {contact?.email && <span className="text-[12px]">{contact.email}</span>}
                    {contact?.primaryPhone && <span className="text-[12px]">{contact.primaryPhone}</span>}
                  </div>
                  <div
                    className="cursor-pointer text-error-main text-[12px] font-bold hover:opacity-70"
                    onClick={async () => {
                      const response = await dispatch(deactivateContactLink(link._id))
                      if (!response.error) {
                        showSnackBar("success", "Co-applicant removed")
                        fetchLinkedContacts()
                      }
                    }}
                  >
                    Remove
                  </div>
                </div>
              )
            })}
          </div>
        </>
      )}
      <div className="border-t border-divider my-2"></div>
      <h6 className="text-[12px] font-semibold uppercase tracking-[0.04em] text-text-secondary">Notes</h6>
      <NotesArea userId={userId} setNoteCreateTrigger={setNoteCreateTrigger} />
      <PopupWithActions
        shadow="shadow-sm"
        anchor={anchorStatuses}
        open={openStatuses}
        handleClose={handleCloseStatuses}
      >
        {
          pickableStatuses?.map((status, key) => (
            <div
              key={key}
              onClick={() => {
                setOpenChangeStatusModal(true)
                setChangeStatusId(status?._id)
              }
              }
              className="cursor-pointer p-[14px] rounded-md text-text-primary hover:bg-surface"
            >
              {status?.label}
            </div>
          ))
        }
      </PopupWithActions>
      <PopupWithActions
        shadow="shadow-sm"
        anchor={anchorPaymentPlan}
        open={!!anchorPaymentPlan}
        handleClose={() => setAnchorPaymentPlan(null)}
      >
        {[{ label: 'Active', active: true }, { label: 'Inactive', active: false }].map((option) => (
          <div
            key={option.label}
            data-testid={`payment-plan-option-${option.label.toLowerCase()}`}
            onClick={() => handlePickPaymentPlan(option.active)}
            className={`cursor-pointer p-[14px] rounded-md hover:bg-surface ${option.active === isPlanActive ? 'text-primary-main font-semibold' : 'text-text-primary'}`}
          >
            {option.label}
          </div>
        ))}
      </PopupWithActions>
      {
        openChangePlanModal &&
        <ConfirmModal leftTopIcon={<LuAlertTriangle size={18} className="text-text-secondary" />}
          className="min-w-[400px] min-h-[200px]"
          title="Warning!"
          modalButtonTitle={"Change"} open={openChangePlanModal}
          setOpen={setOpenChangePlanModal}
          functionButton={handleChangePlanStatus}>
          {`Are you sure you want to change the payment plan to ${planTarget ? 'Active' : 'Inactive'}?`}
        </ConfirmModal>
      }
      {
        openChangeStatusModal && demotionGuardOn && !isReversalPick &&
        <StatusChangeConfirm
          open={openChangeStatusModal}
          setOpen={setOpenChangeStatusModal}
          userId={userId}
          status={pickedStatus}
          onConfirm={handleGuardedChangeStatus} />
      }
      {
        openChangeStatusModal && !(demotionGuardOn && !isReversalPick) &&
        <ConfirmModal leftTopIcon={<LuTrash2 size={18} className="text-text-secondary" />}
          className="min-w-[400px] min-h-[200px]"
          title="Warning!"
          modalButtonTitle={"Change"} open={openChangeStatusModal}
          setOpen={setOpenChangeStatusModal}
          functionButton={handleChangeStatus}>
          {isReversalPick
            ? `Mark this client as ${pickedStatus?.label}? The file stays open — MDL staff can move the client back.`
            : 'Are you sure to change status?'}
        </ConfirmModal>
      }
      <PopupWithActions
        shadow="shadow-sm"
        anchor={anchorDispositions}
        open={openDispositions}
        handleClose={() => { setOpenDispositions(false); setAnchorDispositions(null) }}
      >
        {dispositions?.map((disp, key) => (
          <div
            key={key}
            onClick={() => {
              setOpenChangeDispositionModal(true)
              setChangeDispositionId(disp._id)
              setOpenDispositions(false)
              setAnchorDispositions(null)
            }}
            className="cursor-pointer p-[14px] rounded-md text-text-primary hover:bg-surface"
          >
            {disp.label}
          </div>
        ))}
      </PopupWithActions>
      {openChangeDispositionModal && pipelineOn && (
        <LeadStatusConfirm
          open={openChangeDispositionModal}
          setOpen={setOpenChangeDispositionModal}
          status={pickedDisposition}
          onConfirm={handleChangeLeadStatus} />
      )}
      {openChangeDispositionModal && !pipelineOn && (
        <ConfirmModal
          className="min-w-[400px] min-h-[200px]"
          title="Change Lead Status"
          modalButtonTitle="Change"
          open={openChangeDispositionModal}
          setOpen={setOpenChangeDispositionModal}
          functionButton={handleChangeDisposition}>
          Are you sure you want to change the lead status?
        </ConfirmModal>
      )}
    </Card >
  )
}

export default SideBarInfo